"use client";

import { type ComponentProps, type FC, useCallback, useRef } from "react";
import { cn } from "../../utils";
import SliderContext from "./context";
import useAutoplay from "./hooks/useAutoplay";
import useInitialScroll from "./hooks/useInitialScroll";
import useScrollEdges from "./hooks/useScrollEdges";
import useSliderItems from "./hooks/useSliderItems";
import useWheelBehavior from "./hooks/useWheelBehavior";
import {
  getNextStepIndex,
  getPreviousStepIndex,
  scrollContainerToItem,
} from "./scrolling";

type Props = ComponentProps<"div"> & {
  dir?: "ltr" | "rtl";
  step?: number;
  initialScrollToIndex?: number;
  wheelBehavior?: "scroll-slider" | "scroll-page";
  autoplayDelay?: number;
};

const Slider: FC<Props> = ({
  dir = "ltr",
  step,
  initialScrollToIndex,
  wheelBehavior = "scroll-slider",
  autoplayDelay,
  className,
  children,
  onMouseEnter,
  onMouseLeave,
  ...props
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    registerItem,
    getOrderedItems,
    itemCount,
    firstVisibleIndex,
    lastVisibleIndex,
  } = useSliderItems(scrollRef);
  const { canScrollToStart, canScrollToEnd } = useScrollEdges(scrollRef);

  useWheelBehavior(scrollRef, wheelBehavior);
  useInitialScroll(scrollRef, getOrderedItems, initialScrollToIndex);

  const scrollToIndex = useCallback(
    (index: number) => {
      const target = getOrderedItems()[index];
      if (target) {
        scrollContainerToItem(target, scrollRef.current);
      }
    },
    [getOrderedItems],
  );

  const scrollPrev = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }
    if (step === undefined) {
      container.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    const target =
      getOrderedItems()[getPreviousStepIndex(firstVisibleIndex, step)];
    if (target) {
      scrollContainerToItem(target, container, step);
    }
  }, [step, firstVisibleIndex, getOrderedItems]);

  const scrollNext = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }
    if (step === undefined) {
      const maxScroll = container.scrollWidth - container.clientWidth;
      const sign = dir === "rtl" ? -1 : 1;
      container.scrollTo({ left: sign * maxScroll, behavior: "smooth" });
      return;
    }
    const items = getOrderedItems();
    const target =
      items[getNextStepIndex(lastVisibleIndex, items.length, step)];
    if (target) {
      scrollContainerToItem(target, container, step);
    }
  }, [dir, step, lastVisibleIndex, getOrderedItems]);

  const stepsThroughVisibleItems = step !== undefined;
  const canScrollPrev = stepsThroughVisibleItems
    ? firstVisibleIndex === -1
      ? itemCount > 0
      : firstVisibleIndex > 0
    : canScrollToStart;
  const canScrollNext = stepsThroughVisibleItems
    ? lastVisibleIndex < itemCount - 1
    : canScrollToEnd;

  const { pause, resume } = useAutoplay({
    delay: autoplayDelay,
    canScrollNext,
    scrollNext,
    scrollToIndex,
  });

  return (
    <SliderContext.Provider
      value={{
        scrollRef,
        registerItem,
        scrollPrev,
        scrollNext,
        scrollToIndex,
        canScrollPrev,
        canScrollNext,
        itemCount,
        firstVisibleIndex,
        dir,
      }}
    >
      <div
        className={cn("relative min-w-0", className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="slider"
        data-wheel-behavior={wheelBehavior}
        {...props}
        onMouseEnter={(event) => {
          onMouseEnter?.(event);
          pause();
        }}
        onMouseLeave={(event) => {
          onMouseLeave?.(event);
          resume();
        }}
      >
        {children}
      </div>
    </SliderContext.Provider>
  );
};

export default Slider;
